/**
 * Tipos de dominio y conversión hacia el motor (compartidos por la web y el servidor).
 */
import type { EngineSettings, HabitDef, HabitVersion } from '@habitos/engine';
import type { Entry, Focus, Group, Habit, Journal, ListItem, Move, Note, Override, Reminder, Settings, Vacation } from '@habitos/protocol';

export type WithId<T> = T & { id: string };

export type HabitRec = WithId<Habit>;
export type EntryRec = WithId<Entry>;
export type OverrideRec = WithId<Override>;
export type GroupRec = WithId<Group>;
export type VacationRec = WithId<Vacation>;
export type ReminderRec = WithId<Reminder>;
export type SettingsRec = WithId<Settings>;
export type NoteRec = WithId<Note>;
export type ListItemRec = WithId<ListItem>;
export type JournalRec = WithId<Journal>;
export type FocusRec = WithId<Focus>;
export type MoveRec = WithId<Move>;

export function toHabitDef(h: HabitRec): HabitDef {
  return {
    id: h.id,
    kind: h.kind,
    startDate: h.startDate,
    endDate: h.endDate,
    archivedAt: h.archivedAt,
    versions: h.versions as HabitVersion[],
    dueDate: h.dueDate,
    completedAt: h.completedAt,
  };
}

export function toEngineSettings(s: Settings): EngineSettings {
  return { initialWeekStart: s.weekStartInitial, weekStartChanges: s.weekStartChanges };
}

export function currentVersion(h: Habit): HabitVersion {
  const sorted = [...h.versions].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
  return sorted[sorted.length - 1] as HabitVersion;
}

export function currentWeekStartOf(s: Settings): number {
  const last = s.weekStartChanges[s.weekStartChanges.length - 1];
  return last ? last.weekStart : s.weekStartInitial;
}

export const HABIT_COLORS = ['teal', 'amber', 'coral', 'violet', 'blue', 'green', 'pink', 'slate'] as const;
export type HabitColor = (typeof HABIT_COLORS)[number];

export const HABIT_ICONS = [
  'check',
  'book',
  'dumbbell',
  'droplet',
  'moon',
  'footprints',
  'brain',
  'apple',
  'pill',
  'music',
  'pen',
  'code',
  'leaf',
  'bike',
  'coffee',
  'cigarette-off',
  'wine-off',
  'smartphone',
  'heart',
  'sun',
  'bed',
  'target',
  'languages',
  'wallet',
  'sparkles',
  'utensils',
] as const;
export type HabitIconName = (typeof HABIT_ICONS)[number];

export const UNITS = ['count', 'min', 'h', 'km', 'steps', 'ml', 'l', 'pages', 'kcal', 'g'] as const;
